<template>
  <form class="details-content__form content-form" @submit.prevent="submit">
    <component
      :is="fieldComponent"
      v-for="(fieldComponent, fieldName) in fieldComponents"
      :key="fieldName"
      v-model="formAddress[fieldName]"
      :error="errors ? errors[fieldName] : null"
      :validation-rules="validationRules[fieldName]"
      v-bind="fieldName === 'closing_date' ? { from: formAddress.opening_date } : {}"
      @focusout="validate(fieldName)"
    />
    <span class="content-form__required">{{ trans('addresses.details.form.required') }}</span>
    <button
      class="content-form__submit btn btn--primary"
      type="submit"
      :class="{ 'loading-btn': loading }"
    >
      {{ trans('addresses.details.form.submit') }}
    </button>
  </form>
</template>

<script>
import { markRaw } from 'vue';
import { some, isString, keys, mapKeys, pick, camelCase, snakeCase } from 'lodash-es';
import { useAddressStore } from '@stores/address';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import moment from '@tools/moment';
import requests from '@tools/requests-mixin';
import { URL_XHR_ADDRESS_INFORMATIONS_UPDATE } from '@tools/routes/routes';
import translation from '@elements/translation';
import AddressDetailsFormAccessInfos from './AddressDetailsFormAccessInfos.vue';
import AddressDetailsFormCapacity from './AddressDetailsFormCapacity.vue';
import AddressDetailsFormClosingDate from './AddressDetailsFormClosingDate.vue';
import AddressDetailsFormCommonSurface from './AddressDetailsFormCommonSurface.vue';
import AddressDetailsFormName from './AddressDetailsFormName.vue';
import AddressDetailsFormOpeningDate from './AddressDetailsFormOpeningDate.vue';
import AddressDetailsFormPetsAllowed from './AddressDetailsFormPetsAllowed.vue';
import AddressDetailsFormTotalSurface from './AddressDetailsFormTotalSurface.vue';

export default {
  mixins: [ translation, moment, requests ],
  props: {
    address: { type: Object, required: true },
  },
  setup() {
    const store = useAddressStore();
    return { store };
  },
  data() {
    const initErrors = {
      name: null,
      access_infos: null,
      total_surface: null,
      common_surface: null,
      capacity: null,
      phone_boxes: null,
      opening_date: null,
      closing_date: null,
    };
    return {
      formAddress: {
        ...this.address,
        opening_date: this.address.opening_date ? this.getSimpleDate(this.address.opening_date.iso) : null,
        closing_date: this.address.closing_date ? this.getSimpleDate(this.address.closing_date.iso) : null,
      },
      loading: false,
      saved: false,
      fieldComponents: {
        name: markRaw(AddressDetailsFormName),
        access_infos: markRaw(AddressDetailsFormAccessInfos),
        total_surface: markRaw(AddressDetailsFormTotalSurface),
        common_surface: markRaw(AddressDetailsFormCommonSurface),
        capacity: markRaw(AddressDetailsFormCapacity),
        opening_date: markRaw(AddressDetailsFormOpeningDate),
        closing_date: markRaw(AddressDetailsFormClosingDate),
        pets_allowed: markRaw(AddressDetailsFormPetsAllowed),
      },
      validationRules: {
        name: { min_length: 1, max_length: 200, required: true },
        access_infos: { min_length: 1, max_length: 1000 },
        total_surface: { min: 1, max: 10000000 },
        common_surface: { min: 0, max: 10000000 },
        capacity: { min: 1, max: 50000 },
        opening_date: { valid: this.validateOpeningDate },
        closing_date: { valid: this.validateClosingDate },
      },
      initErrors,
      errors: { ...initErrors },
    };
  },
  computed: {
    formBody() {
      return mapKeys(pick(this.formAddress, keys(this.fieldComponents)), (val, key) => camelCase(key));
    },
    valid() {
      return !some(this.errors, isString);
    },
  },
  mounted() {
    window.onbeforeunload = () => {
      this.validate();
      return this.valid ? null : this.trans('addresses.details.form.validation.unload_alert');
    };
  },
  methods: {
    validateOpeningDate(date) {
      return this.validateDate(date);
    },
    validateClosingDate(date) {
      if (date === null) {
        return true;
      }
      return this.validateDate(date)
        && (
          !this.formAddress.opening_date
          || this.isBefore(this.formAddress.opening_date, date)
          || this.isSame(this.formAddress.opening_date, date, 'day')
        );
    },
    validateDate(date) {
      if (date === null) {
        return false;
      }
      const dateObj = new Date(date);
      return dateObj instanceof Date && !Number.isNaN(dateObj.getTime()) && date === this.getSimpleDate(dateObj);
    },
    validate(fieldToValidate) {
      const fields = (fieldToValidate === undefined) ? keys(this.fieldComponents) : [ fieldToValidate ];
      fields.forEach((field) => {
        this.errors[field] = null;
        const rules = this.validationRules[field] || {};
        const value = this.formAddress[field];

        // Check if the field is required or not
        const isRequired = rules.required === true;

        if (value === null || value === '') {
          if (isRequired) {
            this.errors[field] = this.trans(`addresses.details.form.validation.${field}.required`);
          }
          this.updateStore();
          // If field is not required and is empty, no error should be set
        } else {
          keys(rules).forEach((ruleKey) => {
            let valid;
            switch (ruleKey) {
            case 'min':
              valid = value >= rules[ruleKey];
              break;
            case 'max':
              valid = value <= rules[ruleKey];
              break;
            case 'min_length':
              valid = value.length >= rules[ruleKey];
              break;
            case 'max_length':
              valid = value.length <= rules[ruleKey];
              break;
            case 'valid':
              valid = rules[ruleKey](value);
              break;
            default:
              valid = true; // Assume other validations pass if not specified
            }

            if (valid) {
              this.updateStore();
              return;
            }

            this.errors[field] = this.trans(`addresses.details.form.validation.${field}.${ruleKey}`);
          });
        }
      });
      window.onbeforeunload = () => ((this.saved && this.valid) ? null : this.trans('addresses.details.form.validation.unload_alert'));
    },
    async submit() {
      if (this.loading) {
        return false;
      }
      this.validate();
      if (!this.valid) {
        return false;
      }
      this.saved = false;
      this.errors = { ...this.initErrors };
      this.loading = true;
      try {
        await this.put(URL_XHR_ADDRESS_INFORMATIONS_UPDATE.replace('{id}', this.address.id), this.formBody, { emulateJSON: false });
        flashBagSuccess(this.trans('addresses.details.form.success'));
        this.saved = true;
      } catch (err) {
        this.errors = { ...this.errors, ...mapKeys(err.response.data, (val, key) => snakeCase(key)) };
        flashBagError(this.trans('addresses.details.form.error'));
      }
      this.loading = false;
      return true;
    },
    updateStore() {
      this.store.setAddress(this.formAddress);
    },
  },
};
</script>
